import { useEffect, useRef } from "react";
import { Icon } from "../components/Icon";
import { exposureTotals } from "../lib/exposure.mjs";
import { useLanguage, useStore } from "./useStore";

function DetailMetrics({ row }: { row: any }) {
  const t = exposureTotals(row);
  return (
    <>
      <div className="detail-metrics">
        <div><strong>{t.audio}</strong><span>Heard</span></div>
        <div><strong>{t.reading}</strong><span>Read</span></div>
        <div><strong>{t.reviews}</strong><span>Reviews</span></div>
        <div><strong>{t.plays}</strong><span>Clips</span></div>
      </div>
      <div className="source-breakdown">
        <p>Stories <span>{row?.storyAudio || 0} heard · {row?.storyReads || 0} read</span></p>
        <p>Word study & audio <span>{row?.studyAudio || 0} heard · {row?.studyReads || 0} read</span></p>
      </div>
    </>
  );
}

/** Modal for a word or character. Uses the native <dialog> for focus trapping and Escape. */
export function DetailDialog() {
  const store = useStore();
  const ctx = useLanguage();
  const ref = useRef<HTMLDialogElement>(null);
  const { detail } = store;
  const { definition: d, deck, progress: p } = ctx;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (detail && !dialog.open) dialog.showModal();
    if (!detail && dialog.open) dialog.close();
  }, [detail]);

  const close = () => store.closeDetail();
  const word = detail?.type === "word" ? deck.find((w) => w.id === detail.id) : undefined;
  const char = detail?.type === "character" ? detail.id : undefined;
  const related = char ? deck.filter((w) => w.word.includes(char)) : [];

  return (
    <dialog
      id="detail-dialog" ref={ref} onClose={close}
      onClick={(e) => {
        if (e.target !== ref.current) return;
        const r = ref.current.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) ref.current.close();
      }}
    >
      {word && (
        <>
          <div className="dialog-heading">
            <div>
              <p className="overline">A closer look</p>
              <h2 lang={d.id}>{word.word}</h2>
              {d.capabilities.reading && <p className="reading" lang={d.id}>{word.reading}</p>}
            </div>
            <button className="icon-button" onClick={() => ref.current?.close()} aria-label="Close word details"><Icon name="close" /></button>
          </div>
          {p.prefs.english && <p className="detail-meaning">{word.meaning}</p>}
          <p lang={d.id}>{word.targetDefinition}</p>
          <DetailMetrics row={p.exposure.words[word.id]} />
          <div className="button-row">
            <button className="button" onClick={() => { ref.current?.close(); ctx.studyWords([word.id], "Focused word review"); }}>Study this word</button>
            <button className="button secondary" onClick={() => ctx.playWord(word, false)}><Icon name="sound" /> Listen</button>
          </div>
        </>
      )}
      {char && (
        <>
          <div className="dialog-heading">
            <div><p className="overline">Character encounters</p><h2 lang={d.id}>{char}</h2></div>
            <button className="icon-button" onClick={() => ref.current?.close()} aria-label="Close character details"><Icon name="close" /></button>
          </div>
          <DetailMetrics row={p.exposure.characters[char]} />
          <h3>Words with this character</h3>
          <div className="character-words">
            {related.map((w) => (
              <button key={w.id} lang={d.id} onClick={() => { ref.current?.close(); ctx.openWord(w.id); }}>{w.word}</button>
            ))}
          </div>
          {related.length
            ? <button className="button" onClick={() => { ref.current?.close(); ctx.studyWords(related.map((w) => w.id), char + " · character review"); }}>Review these {related.length} words</button>
            : <p className="hint">This character appears in supporting story vocabulary.</p>}
        </>
      )}
    </dialog>
  );
}
